import type { EUserType, IUserInfo } from "@/account/account-models";
import { isFeaturedToFeatured } from "@/chats/chats-helpers";
import type { IMessageInfo } from "@/chats/messenger/messenger-models";
import { EMessageType } from "@/chats/messenger/messenger-models";
import { MESSAGE_GROUPING_WINDOW_MINUTES } from "@/globals";
import { EMessageBubbleType } from "@/models";
import { defined } from "@/utils/variable-evaluation";
import { differenceInMinutes } from "date-fns";

// The list key of a message. Stays the same while a message goes from a
// client-side copy to the one the server confirmed, so it never remounts.
export function getMessageKey(message: IMessageInfo): string {
  return (
    message.clientSideKey ||
    message._id ||
    message.clientSideReceiveId ||
    message.__clientSideSendId ||
    ""
  );
}

export function isPaidMedia(message: IMessageInfo) {
  return (
    message.is_paid &&
    ((defined(message.price) && message.price! > 0) ||
      (defined(message.credits) && message.credits! > 0)) &&
    (message.type === EMessageType.Video ||
      message.type === EMessageType.Image ||
      (message.type === EMessageType.Text && message.is_deleted))
  );
}

export function isCallMessage(message: IMessageInfo) {
  return (
    message.type === EMessageType.CallEnded ||
    message.type === EMessageType.CallMissed ||
    message.type === EMessageType.CallRejected ||
    message.type === EMessageType.CallCanceled
  );
}

export function isLockedTextMessage(message: IMessageInfo) {
  return message.type === EMessageType.Text && message.is_locked === true;
}

const FILLER_AVG_WORD_LENGTH = 5;
const FILLER_SIZE_RATIO = 0.85;
const MAX_FILLER_CHARS = 600;

function buildLockedMessageFiller(chars: number) {
  const words: string[] = [];
  let remaining = Math.min(
    Math.round(chars * FILLER_SIZE_RATIO),
    MAX_FILLER_CHARS
  );
  while (remaining > 0) {
    const wordLength = Math.min(remaining, FILLER_AVG_WORD_LENGTH);
    words.push("a".repeat(wordLength));
    remaining -= wordLength;
  }
  return words.join(" ");
}

export function lockedMessageFillerText(
  chars?: number,
  unlockPrice?: number,
  creditRate?: number
) {
  if (chars != null && chars > 0) return buildLockedMessageFiller(chars);
  if (unlockPrice != null && unlockPrice > 0) {
    const rate = creditRate && creditRate > 0 ? creditRate : 1;
    return buildLockedMessageFiller((unlockPrice / rate) * 50);
  }
  return buildLockedMessageFiller(20);
}

export function isUnpaidContent(
  message: IMessageInfo,
  userType: EUserType,
  otherUserType: EUserType
) {
  if (
    isLockedTextMessage(message) &&
    !isFeaturedToFeatured(userType, otherUserType)
  ) {
    return true;
  }
  return (
    !message.is_paid &&
    message.price! > 0 &&
    !isFeaturedToFeatured(userType, otherUserType)
  );
}

export function isLastRead(
  message: IMessageInfo,
  index: number,
  messages: IMessageInfo[]
) {
  return (
    (message.is_read && !defined(messages[index + 1])) ||
    (message.is_read &&
      defined(messages[index + 1]) &&
      !messages[index + 1].is_read)
  );
}

export function lastReadSentMessage(
  messages: IMessageInfo[],
  userId: string
): number | null {
  let index = messages.length - 1;
  for (; index >= 0; index--) {
    if (
      messages[index].is_read === true &&
      messages[index].sender_id === userId
    ) {
      return index;
    }
  }
  return null;
}

export function messageBubbleType(
  message: IMessageInfo,
  selectedChatUser: IUserInfo
): EMessageBubbleType {
  if (
    message.type === EMessageType.TipUser ||
    message.type === EMessageType.TipModel
  ) {
    return EMessageBubbleType.Tip;
  }
  if (message.is_system && !isCallMessage(message)) {
    if (message.type === EMessageType.IntroEnded) {
      return EMessageBubbleType.SystemWarning;
    }
    if (message.type === EMessageType.ForModelPaidChatRequested) {
      return EMessageBubbleType.SystemPaidChatRequested;
    }
    if (message.type === EMessageType.ForModelPaidChatAccepted) {
      return EMessageBubbleType.SystemPaidChatAccepted;
    }
    return EMessageBubbleType.System;
  }
  if (message.sender_id === selectedChatUser._id) {
    return EMessageBubbleType.OtherUser;
  }
  return EMessageBubbleType.User;
}

export function isMessagePartOfGroup(
  message: IMessageInfo,
  previousMessage?: IMessageInfo
): boolean {
  if (!previousMessage) {
    // first message in the chat
    return false;
  }

  if (!message._id && !message.parent_message) {
    // client side message (not a reply)
    return message.sender_id === previousMessage.sender_id;
  }

  if (
    previousMessage &&
    ((message.is_system && previousMessage.is_system) ||
      (!message.is_system && !previousMessage.is_system)) &&
    message.sender_id === previousMessage.sender_id &&
    differenceInMinutes(message.created_at!, previousMessage.created_at!) <
      MESSAGE_GROUPING_WINDOW_MINUTES &&
    !message.parent_message
  ) {
    // same sender, same type (system or not), within 5 minutes, and not a reply
    return true;
  }

  return false;
}

export function isLastMessageInGroup(
  message: IMessageInfo,
  nextMessage?: IMessageInfo
) {
  return !nextMessage || !isMessagePartOfGroup(nextMessage, message);
}
